import React, { createContext, useContext, useState, ReactNode } from 'react';

interface WindowData {
  id: string;
  title: string;
  content: ReactNode;
  x: number;
  y: number;
  width: number;
  height: number;
  isMinimized: boolean;
  isMaximized: boolean;
  isActive: boolean;
  zIndex: number;
}

interface WindowContextType {
  windows: WindowData[];
  openWindow: (window: Omit<WindowData, 'isMinimized' | 'isMaximized' | 'isActive' | 'zIndex'>) => void;
  closeWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  maximizeWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  updateWindow: (id: string, updates: Partial<WindowData>) => void;
}

const WindowContext = createContext<WindowContextType | undefined>(undefined);

export const useWindows = () => {
  const context = useContext(WindowContext);
  if (!context) {
    throw new Error('useWindows must be used within a WindowProvider');
  }
  return context;
};

export const WindowProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [windows, setWindows] = useState<WindowData[]>([]);
  const [nextZIndex, setNextZIndex] = useState(1000);

  const openWindow = (windowData: Omit<WindowData, 'isMinimized' | 'isMaximized' | 'isActive' | 'zIndex'>) => {
    // Check if window already exists
    const existingWindow = windows.find(w => w.id === windowData.id);
    if (existingWindow) {
      focusWindow(windowData.id);
      return;
    }

    const newWindow: WindowData = {
      ...windowData,
      isMinimized: false,
      isMaximized: false,
      isActive: true,
      zIndex: nextZIndex,
    };

    setWindows(prev => [
      ...prev.map(w => ({ ...w, isActive: false })),
      newWindow,
    ]);
    setNextZIndex(prev => prev + 1);
  };

  const closeWindow = (id: string) => {
    setWindows(prev => prev.filter(w => w.id !== id));
  };

  const minimizeWindow = (id: string) => {
    setWindows(prev =>
      prev.map(w =>
        w.id === id ? { ...w, isMinimized: true, isActive: false } : w
      )
    );
  };

  const maximizeWindow = (id: string) => {
    setWindows(prev =>
      prev.map(w =>
        w.id === id ? { ...w, isMaximized: !w.isMaximized, isActive: true } : { ...w, isActive: false }
      )
    );
  };

  const focusWindow = (id: string) => {
    setWindows(prev =>
      prev.map(w => ({
        ...w,
        isActive: w.id === id,
        isMinimized: w.id === id ? false : w.isMinimized,
        zIndex: w.id === id ? nextZIndex : w.zIndex,
      }))
    );
    setNextZIndex(prev => prev + 1);
  };

  const updateWindow = (id: string, updates: Partial<WindowData>) => {
    setWindows(prev =>
      prev.map(w => (w.id === id ? { ...w, ...updates } : w))
    );
  };

  return (
    <WindowContext.Provider
      value={{
        windows,
        openWindow,
        closeWindow,
        minimizeWindow,
        maximizeWindow,
        focusWindow,
        updateWindow,
      }}
    >
      {children}
    </WindowContext.Provider>
  );
};